import React, { useState, useMemo } from 'react';
import { Button, Descriptions, Table, Space, Card, Typography, message, Tag, Alert } from 'antd';
import { useLoaderData, useRevalidator, Link } from 'react-router-dom';
import FulfillmentStatusLabel from '@shared/fulfillments/FulfillmentStatusLabel';
import ChangeShippingTypeModalImpl from '@shared/fulfillments/ChangeShippingTypeModal';
import ListPageHeader from '@shared/ListPageHeader';
import VideoSection from '@shared/VideoSection';
import { fulfillmentService } from '@services/operator/fulfillmentService';
import { useOperatorNotification } from '@contexts/operator/OperatorNotificationContext';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@utils/dateTime';
import { LinkOutlined, LockOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import useInventoryModal from '@hooks/shared/useInventoryModal';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';

const { Text } = Typography;

interface FulfillmentOrder {
  id: string;
  name?: string;
  status?: string;
  receiver_name?: string;
  sales_channel?: string;
  note?: string;
  [key: string]: unknown;
}

interface FulfillmentItemInventory {
  [key: string]: unknown;
}

interface FulfillmentItem {
  id: string;
  order_id?: string;
  quantity?: number;
  product?: { sku?: string; name?: string; [key: string]: unknown };
  unknown_product?: { sku?: string; name?: string; [key: string]: unknown };
  inventories?: FulfillmentItemInventory[];
  [key: string]: unknown;
}

interface FulfillmentShipment {
  id: string;
  tracking_number?: string;
  status?: string;
  source?: string;
  deleted_at?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

interface FulfillmentWorkOrder {
  id: string;
  name?: string;
  status?: string;
  [key: string]: unknown;
}

interface PickingListSummary {
  id: string;
  name?: string;
  status?: string;
}

interface PickingWaveSummary {
  id: string;
  name?: string;
  picking_lists?: PickingListSummary[];
}

interface Fulfillment {
  id: string;
  number?: string;
  status?: string;
  merchant_id?: string;
  merchant_name?: string;
  is_cod?: boolean;
  warehouse?: { name?: string; [key: string]: unknown };
  shipping_type?: { code?: string; name?: string; [key: string]: unknown };
  created_at?: string;
  total_amount?: string | number | null;
  currency?: string;
  packing_locked?: boolean;
  packing_operator_name?: string;
  source_type?: string;
  stock_transfer_order?: { id: string; name?: string; status?: string; [key: string]: unknown };
  work_orders?: FulfillmentWorkOrder[];
  picking_wave?: PickingWaveSummary | null;
  orders?: FulfillmentOrder[];
  items?: FulfillmentItem[];
  shipments?: FulfillmentShipment[];
  [key: string]: unknown;
}

type BreadcrumbItem = string | { label: string };

const FulfillmentShow: React.FC = () => {
  // ChangeShippingTypeModal 仍是 .jsx，TS 從其 destructure 推導出的 props 不完整，
  // 用 ComponentType<any> 包一層保留原本 runtime 行為。放在 component body 內以避免 Vite HMR 的 top-level TDZ。
  const ChangeShippingTypeModal = ChangeShippingTypeModalImpl as React.ComponentType<any>;
  const fulfillment = useLoaderData() as Fulfillment;
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  // Wire the project's background-job notification flow so the VideoSection
  // rescue button can register its job_id; jobs surface in the right-corner
  // panel and emit completion notifications.
  const { addJob } = useOperatorNotification();

  const [changeShippingTypeVisible, setChangeShippingTypeVisible] = useState(false);

  // 拉 inventory_attribute_definitions API 回的 name → label 餵給 useInventoryModal，
  // 避免出貨單 Show 表格與庫存分配 modal 顯示英文 raw key（例：manufacturing_plant）。
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', fulfillment.merchant_id);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label || def.name])),
    [attributeDefinitions],
  );

  const {
    fulfillmentItemColumns,
    showAllInventories,
    renderInventoryModals,
  } = useInventoryModal({ attributeLabelByName });

  const isMerged = (fulfillment.orders?.length ?? 0) > 1;

  // 按訂單分組 items
  const itemsByOrder = useMemo(() => {
    const items = fulfillment.items || [];
    const orders = fulfillment.orders || [];
    if (orders.length <= 1) return null;

    const groups: Record<string, FulfillmentItem[]> = {};
    items.forEach((item) => {
      const oid = item.order_id || '_unknown';
      if (!groups[oid]) groups[oid] = [];
      groups[oid].push(item);
    });

    return orders.map((order) => ({
      order,
      items: groups[order.id] || []
    })).filter((g) => g.items.length > 0);
  }, [fulfillment]);

  // 彙整所有品項的庫存佔用（帶商品資訊）
  const allInventories = useMemo(() => {
    const items = fulfillment.items || [];
    return items.flatMap((item) =>
      (item.inventories || []).map((inv) => ({
        ...inv,
        product_sku: item.product?.sku || item.unknown_product?.sku || '-',
        product_name: item.product?.name || item.unknown_product?.name || '-',
      }))
    );
  }, [fulfillment]);

  const canChangeShippingType = ['pending', 'allocated', 'partial_allocated', 'out_of_stock'].includes(fulfillment?.status ?? '');

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    switch (fulfillment.source_type) {
      case 'stock_transfer_order':
        return ['stock_transfer_orders', 'stock_transfer_orders.list', { label: fulfillment.number ?? '' }];
      case 'work_order':
        return ['production', 'production.work_orders', { label: fulfillment.number ?? '' }];
      default:
        return ['orders', 'orders.fulfillments', { label: fulfillment.number ?? '' }];
    }
  }, [fulfillment.source_type, fulfillment.number]);

  const renderSourceCard = () => {
    switch (fulfillment.source_type) {
      case 'stock_transfer_order':
        if (!fulfillment.stock_transfer_order) return null;
        return (
          <Card title={<Tag color="purple">{t('enums.fulfillment.source_type.StockTransferOrder')}</Tag>} size="small">
            <div style={{ padding: '8px 0' }}>
              <Link to={`/stock_transfer_orders/${fulfillment.stock_transfer_order.id}`} style={{ fontWeight: 'bold' }}>
                {fulfillment.stock_transfer_order.name}
              </Link>
              <Tag style={{ marginLeft: 8 }}>{t(`shared.statuses.${fulfillment.stock_transfer_order.status}`, fulfillment.stock_transfer_order.status ?? '') as string}</Tag>
            </div>
          </Card>
        );
      case 'work_order':
        if (!fulfillment.work_orders?.length) return null;
        return (
          <Card title={<Tag color="orange">{t('enums.fulfillment.source_type.WorkOrderItem')}</Tag>} size="small">
            {fulfillment.work_orders.map((wo) => (
              <div key={wo.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Space>
                  <Link to={`/work_orders/${wo.id}`} style={{ fontWeight: 'bold' }}>{wo.name}</Link>
                  <Tag>{t(`shared.statuses.${wo.status}`, wo.status ?? '') as string}</Tag>
                </Space>
              </div>
            ))}
          </Card>
        );
      default:
        return (
          <Card title={t('operator.fulfillments.list.columns.source_documents')} size="small">
            {fulfillment.orders?.map((order) => (
              <div key={order.id} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Space>
                  <Link to={`/orders/${order.id}`} style={{ fontWeight: 'bold' }}>{order.name}</Link>
                  {order.sales_channel && <Tag>{order.sales_channel}</Tag>}
                  <Text type="secondary">{order.receiver_name}</Text>
                  {order.note && <Text type="secondary">({order.note})</Text>}
                </Space>
              </div>
            ))}
          </Card>
        );
    }
  };

  // summary 欄位數：SKU, 名稱, 庫存類別, 批號, 效期, 製造日, 自訂屬性 = 7
  const summaryColSpan = 7;

  const renderSummary = (pageData: readonly FulfillmentItem[]) => {
    const totalQty = pageData.reduce((sum, item) => sum + (item.quantity || 0), 0);
    return (
      <Table.Summary>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={summaryColSpan}>
            <strong style={{ fontSize: 16 }}>{t('common.total')}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={summaryColSpan} align="right">
            {allInventories.length > 0 ? (
              <Button type="link" size="small" style={{ padding: 0, fontSize: 28, fontWeight: 'bold' }} onClick={() => showAllInventories(allInventories)}>
                {totalQty}
              </Button>
            ) : (
              <strong style={{ fontSize: 28 }}>{totalQty}</strong>
            )}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={summaryColSpan + 1} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div>
      <ListPageHeader
        items={breadcrumbItems}
      />

      <Descriptions bordered column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }} size="small" style={{ marginBottom: '16px' }}>
        <Descriptions.Item label={t('shared.id')}>{fulfillment.number}</Descriptions.Item>
        <Descriptions.Item label={t('shared.status')}>
          <FulfillmentStatusLabel status={fulfillment.status} />
        </Descriptions.Item>
        <Descriptions.Item label={t('shared.merchant')}>{fulfillment.merchant_name}</Descriptions.Item>
        <Descriptions.Item label={t('shared.warehouse')}>{fulfillment.warehouse?.name}</Descriptions.Item>
        {fulfillment.source_type === 'order' && (
        <Descriptions.Item label={t('models.order.shipping_type')}>
          <Space>
            <span>
              {fulfillment.shipping_type
                ? t(`shipping_types.${fulfillment.shipping_type.code}`, { defaultValue: fulfillment.shipping_type.name }) as string
                : '-'}
            </span>
            {canChangeShippingType && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  const hasBlockingShipment = (fulfillment.shipments || []).some(
                    (s) => !s.deleted_at && (s.source === 'manual_uploaded' || s.source === 'platform')
                  );
                  if (hasBlockingShipment) {
                    message.warning(t('shared.fulfillments.change_shipping_type.has_manual_shipment', {
                      defaultValue: '此 fulfillment 有手動/平台託運單，無法變更物流類型',
                    }) as string);
                    return;
                  }
                  setChangeShippingTypeVisible(true);
                }}
              >
                {t('shared.fulfillments.change_shipping_type.button', { defaultValue: '變更' }) as string}
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        )}
        <Descriptions.Item label={t('common.created_at')}>{formatDateTime(fulfillment.created_at ?? '')}</Descriptions.Item>
        <Descriptions.Item label={t('models.fulfillment.total_amount', { defaultValue: '金額' })}>
          {fulfillment.total_amount != null
            ? <>{Number(fulfillment.total_amount).toFixed(2)} {fulfillment.currency}</>
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.fulfillment.is_cod')}>
          {fulfillment.is_cod
            ? <CheckOutlined style={{ color: 'green' }} />
            : <CloseOutlined style={{ color: 'lightgrey' }} />}
        </Descriptions.Item>
      </Descriptions>

      {fulfillment.packing_locked && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          icon={<LockOutlined />}
          message={t('operator.fulfillments.detail.qc_locked_by', { operator_name: fulfillment.packing_operator_name })}
        />
      )}

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {renderSourceCard()}

        {fulfillment.picking_wave && (
          <Card title={t('models.picking_wave._', { defaultValue: '揀貨波次' }) as string} size="small">
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space>
                <LinkOutlined />
                <Link to={`/picking_waves/${fulfillment.picking_wave.id}`} style={{ fontWeight: 'bold' }}>
                  {fulfillment.picking_wave.name}
                </Link>
              </Space>
              {(fulfillment.picking_wave.picking_lists?.length ?? 0) > 0 && (
                <Space wrap>
                  <Text type="secondary">{t('models.picking_list.other', { defaultValue: '揀貨單' }) as string}：</Text>
                  {fulfillment.picking_wave.picking_lists?.map((pl) => (
                    <Space key={pl.id} size={4}>
                      <Link to={`/picking_lists/${pl.id}`}>{pl.name}</Link>
                      {pl.status && (
                        <Tag>{t(`enums.picking_list.status.${pl.status}`, pl.status) as string}</Tag>
                      )}
                    </Space>
                  ))}
                </Space>
              )}
            </Space>
          </Card>
        )}

        {/* 品項列表 - 按訂單分組（合併單）或平鋪 */}
        {isMerged && itemsByOrder ? (
          <>
            <Alert
              message={t('shared.orders.detail.fulfillments.merged_warning')}
              type="info"
              showIcon
              style={{ marginBottom: 0 }}
            />
            {itemsByOrder.map(({ order, items }: { order: FulfillmentOrder; items: FulfillmentItem[] }) => (
              <Card
                key={order.id}
                title={
                  <Space>
                    <LinkOutlined />
                    <span>{t('models.order._')}:</span>
                    <Link to={`/orders/${order.id}`}>{order.name}</Link>
                    {order.receiver_name && <Text type="secondary">{order.receiver_name}</Text>}
                  </Space>
                }
                size="small"
                style={{ borderColor: '#d9d9d9' }}
              >
                <Table
                  dataSource={items}
                  columns={fulfillmentItemColumns as any}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  summary={renderSummary}
                />
              </Card>
            ))}
          </>
        ) : (
          <Card title={t('shared.items')} size="small">
            <Table
              dataSource={fulfillment.items}
              columns={fulfillmentItemColumns as any}
              rowKey="id"
              pagination={false}
              size="small"
              summary={renderSummary}
            />
          </Card>
        )}

        {(fulfillment.shipments?.length ?? 0) > 0 && (
          <Card title={t('operator.menu.shipments.title')} size="small">
            <Table
              dataSource={fulfillment.shipments}
              columns={[
                { title: t('models.shipment.tracking_number'), dataIndex: 'tracking_number', key: 'tracking_number' },
                { title: t('shared.status'), dataIndex: 'status', key: 'status', render: (val: string) => val ? t(`enums.shipment.status.${val}`, { defaultValue: val }) : '-' },
                { title: t('common.created_at'), dataIndex: 'created_at', key: 'created_at', render: (val: string) => formatDateTime(val) },
              ]}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        )}

        {/* QC 影片 + 錄影 session 診斷（與 OrderShow 共用 VideoSection） */}
        <VideoSection
          resourceId={fulfillment.id}
          service={fulfillmentService}
          addJob={addJob}
        />
      </Space>

      {renderInventoryModals()}

      <ChangeShippingTypeModal
        visible={changeShippingTypeVisible}
        onCancel={() => setChangeShippingTypeVisible(false)}
        onSuccess={() => {
          setChangeShippingTypeVisible(false);
          revalidator.revalidate();
        }}
        fulfillment={fulfillment}
        role="operator"
      />
    </div>
  );
};

export default FulfillmentShow;
